import { AnalyticsUserEvent } from '@fruitful/types';
import { useCallback, useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonVariant,
    H5Regular,
    SelectSingle,
    Sheet,
    YStack,
} from 'smoothie';

import { useViewEvents } from '../../events/hooks/useViewEvents';
import { track } from '../../logger/Logger';
import { useSaveNoBookingReason } from '../hooks/useSaveNoBookingReason';
import {
    BOOK_FURTHER_OUT_OPTIONS,
    type BookFurtherOutOption,
} from '../types/BookFurtherOut';

const ENTRY_REASON = 'SCHEDULE';

export const WhyBookFurtherOutSheet = ({
    isOpen,
    onClose,
    onSavedAndContinue,
    daysThreshold = 7,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSavedAndContinue: (option: BookFurtherOutOption) => void;
    daysThreshold?: number;
}) => {
    const { trackViewOnce } = useViewEvents();
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const { saveNoBookingReason } = useSaveNoBookingReason();

    const onCloseWithoutAnswer = useCallback(() => {
        track(
            AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_DISMISS_BOOK_FURTHER_OUT,
        );
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedIndex(null);
        }
    }, [isOpen]);

    const persistSelectionAndContinue = (index: number) => {
        const option = BOOK_FURTHER_OUT_OPTIONS[index];
        if (option == null) {
            return;
        }
        setSelectedIndex(index);
        track(AnalyticsUserEvent.MAGIC_SAVE_DIDNT_BOOK_SCHEDULE, {
            entryReason: ENTRY_REASON,
            detailReason: option.detailReason,
        });
        void saveNoBookingReason(`${ENTRY_REASON}: ${option.detailReason}`);
        onSavedAndContinue(option);
        onClose();
    };

    if (isOpen) {
        trackViewOnce(
            'Onboarding/BookAvailableGuides/BookFurtherOutFrictionSheet',
        );
    }

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onCloseWithoutAnswer}
            dismissOnOverlayPress={false}
            showCloseButton={true}
            disableDrag={true}
        >
            <YStack
                gap={24}
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                alignSelf="center"
            >
                <YStack
                    gap={8}
                    alignItems="center"
                    width="100%"
                    maxWidth={300}
                    alignSelf="center"
                >
                    <H5Regular textAlign="center" color="$gray1200">
                        Is there a reason you want to book further out?
                    </H5Regular>
                    <BodyMediumRegular textAlign="center" color="$gray1000">
                        We typically only show {daysThreshold} days of
                        availability for each Guide.
                    </BodyMediumRegular>
                </YStack>
                <YStack gap={12} width="100%">
                    <SelectSingle
                        options={BOOK_FURTHER_OUT_OPTIONS.map((option) => ({
                            title: option.title,
                        }))}
                        selectedIndex={selectedIndex}
                        onSelect={(index) => {
                            persistSelectionAndContinue(index);
                        }}
                    />
                </YStack>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text="Back"
                    onPress={onCloseWithoutAnswer}
                />
            </YStack>
        </Sheet>
    );
};
